import React, { ReactNode } from 'react';

import styled from 'styled-components';

import { useMobileContext } from '../MobileContext';

export interface ViewerLayoutProps {
  /** Main viewer content */
  children: ReactNode;
  /** Optional breadcrumb navigation */
  breadcrumb?: ReactNode;
  /** Optional toolbar/controls */
  toolbar?: ReactNode;
  /** Optional left sidebar for navigation/metadata */
  leftSidebar?: ReactNode;
  /** Optional right sidebar for details/properties */
  rightSidebar?: ReactNode;
  /** Optional footer for actions/status */
  footer?: ReactNode;
  /** Custom className for styling */
  className?: string;

  /** Layout configuration */
  layoutConfig?: {
    /** Left sidebar width (default: 300px) */
    leftSidebarWidth?: string;
    /** Right sidebar width (default: 350px) */
    rightSidebarWidth?: string;
    /** Show full-height layout (no padding) */
    fullHeight?: boolean;
    /** Hide overflow (for image/document viewers) */
    hideOverflow?: boolean;
  };

  /** Mobile configuration */
  mobileConfig?: {
    /** Hide sidebars on mobile and show as overlays */
    overlaySidebarsOnMobile?: boolean;
    /** Stack toolbar elements vertically on mobile */
    stackToolbarOnMobile?: boolean;
    /** Hide breadcrumb on mobile */
    hideBreadcrumbOnMobile?: boolean;
  };
}

/**
 * Generic layout component for viewer interfaces (file viewers, document readers, etc.).
 * Provides a structured layout with optional navigation, toolbars, and sidebars.
 *
 * Features:
 * - Responsive viewer behavior
 * - Optional breadcrumb navigation
 * - Flexible sidebar positioning
 * - Mobile-optimized overlays
 * - Full-height content support
 *
 * @example
 * // Basic document viewer
 * <ViewerLayout
 *   breadcrumb={<Breadcrumb />}
 *   toolbar={<ViewerToolbar />}
 *   rightSidebar={<DocumentProperties />}
 *   layoutConfig={{ fullHeight: true, hideOverflow: true }}
 * >
 *   <DocumentViewer />
 * </ViewerLayout>
 *
 * @example
 * // Image viewer with metadata sidebar
 * <ViewerLayout
 *   leftSidebar={<ImageMetadata />}
 *   toolbar={<ImageControls />}
 *   mobileConfig={{ overlaySidebarsOnMobile: true }}
 * >
 *   <ImageViewer />
 * </ViewerLayout>
 */
export const ViewerLayout: React.FC<ViewerLayoutProps> = ({
  children,
  breadcrumb,
  toolbar,
  leftSidebar,
  rightSidebar,
  footer,
  className,
  layoutConfig = {},
  mobileConfig = {},
}) => {
  const { isMobile } = useMobileContext();

  const { leftSidebarWidth = '300px', rightSidebarWidth = '350px', fullHeight = false, hideOverflow = false } = layoutConfig;

  const { overlaySidebarsOnMobile = true, stackToolbarOnMobile = true, hideBreadcrumbOnMobile = false } = mobileConfig;

  // Determine what to show based on mobile config
  const showBreadcrumb = breadcrumb && !(isMobile && hideBreadcrumbOnMobile);
  const showLeftSidebar = leftSidebar && !(isMobile && overlaySidebarsOnMobile);
  const showRightSidebar = rightSidebar && !(isMobile && overlaySidebarsOnMobile);

  // Generate grid template areas
  const getGridTemplateAreas = () => {
    const leftCol = showLeftSidebar ? 'left-sidebar' : '.';
    const rightCol = showRightSidebar ? 'right-sidebar' : '.';

    let areas = '';
    if (showBreadcrumb) {
      areas += `"${leftCol} breadcrumb ${rightCol}"\n`;
    }
    if (toolbar) {
      areas += `"${leftCol} toolbar ${rightCol}"\n`;
    }
    areas += `"${leftCol} content ${rightCol}"`;
    if (footer) {
      areas += `\n"${leftCol} footer ${rightCol}"`;
    }

    return areas;
  };

  // Generate grid template columns
  const getGridTemplateColumns = () => {
    const leftCol = showLeftSidebar ? leftSidebarWidth : '';
    const rightCol = showRightSidebar ? rightSidebarWidth : '';
    const centerCol = '1fr';

    return [leftCol, centerCol, rightCol].filter(Boolean).join(' ');
  };

  // Generate grid template rows
  const getGridTemplateRows = () => {
    const rows = [];
    if (showBreadcrumb) rows.push('auto');
    if (toolbar) rows.push('auto');
    rows.push('1fr'); // content gets remaining space
    if (footer) rows.push('auto');
    return rows.join(' ');
  };

  return (
    <StyledViewerLayout
      className={className}
      $isMobile={isMobile}
      $fullHeight={fullHeight}
      $hideOverflow={hideOverflow}
      style={{
        gridTemplateAreas: getGridTemplateAreas(),
        gridTemplateColumns: getGridTemplateColumns(),
        gridTemplateRows: getGridTemplateRows(),
      }}
    >
      {/* Breadcrumb */}
      {showBreadcrumb && (
        <StyledBreadcrumb $isMobile={isMobile} style={{ gridArea: 'breadcrumb' }}>
          {breadcrumb}
        </StyledBreadcrumb>
      )}

      {/* Toolbar */}
      {toolbar && (
        <StyledToolbar $isMobile={isMobile} $stackOnMobile={stackToolbarOnMobile} style={{ gridArea: 'toolbar' }}>
          {toolbar}
        </StyledToolbar>
      )}

      {/* Left Sidebar */}
      {showLeftSidebar && (
        <StyledSidebar $position="left" $isMobile={isMobile} style={{ gridArea: 'left-sidebar' }}>
          {leftSidebar}
        </StyledSidebar>
      )}

      {/* Main Viewer Content */}
      <StyledViewerContent $isMobile={isMobile} $fullHeight={fullHeight} $hideOverflow={hideOverflow} style={{ gridArea: 'content' }}>
        {children}
      </StyledViewerContent>

      {/* Right Sidebar */}
      {showRightSidebar && (
        <StyledSidebar $position="right" $isMobile={isMobile} style={{ gridArea: 'right-sidebar' }}>
          {rightSidebar}
        </StyledSidebar>
      )}

      {/* Footer */}
      {footer && (
        <StyledFooter $isMobile={isMobile} style={{ gridArea: 'footer' }}>
          {footer}
        </StyledFooter>
      )}

      {/* Mobile Overlay Sidebars */}
      {isMobile && overlaySidebarsOnMobile && (
        <>
          {leftSidebar && (
            <StyledMobileOverlay $position="left">
              <StyledSidebar $position="left" $isMobile={true}>
                {leftSidebar}
              </StyledSidebar>
            </StyledMobileOverlay>
          )}
          {rightSidebar && (
            <StyledMobileOverlay $position="right">
              <StyledSidebar $position="right" $isMobile={true}>
                {rightSidebar}
              </StyledSidebar>
            </StyledMobileOverlay>
          )}
        </>
      )}
    </StyledViewerLayout>
  );
};

ViewerLayout.displayName = 'ViewerLayout';

// Styled Components
interface StyledProps {
  $isMobile: boolean;
  $fullHeight?: boolean;
  $hideOverflow?: boolean;
  $stackOnMobile?: boolean;
  $position?: 'left' | 'right';
}

const StyledViewerLayout = styled.div<StyledProps>`
  /* CSS Grid layout for structured viewer interface */
  display: grid;
  min-height: 0;

  /* Conditional height and overflow behavior */
  height: ${({ $fullHeight }) => ($fullHeight ? '100%' : 'auto')};
  overflow: ${({ $hideOverflow }) => ($hideOverflow ? 'hidden' : 'auto')};

  /* No gaps for seamless viewer experience */
  gap: 0;
`;

const StyledBreadcrumb = styled.div<StyledProps>`
  /* Breadcrumb navigation area */
  padding: ${({ theme }) => `${theme.space.spacing10} ${theme.space.spacing20}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};

  /* Mobile adjustments */
  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.space.spacing10} ${theme.space.spacing10}`};
  }
`;

const StyledToolbar = styled.div<StyledProps>`
  /* Toolbar/controls area */
  padding: ${({ theme }) => `${theme.space.spacing10} ${theme.space.spacing20}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
  background-color: ${({ theme }) => theme.colors.palette.white};

  /* Mobile responsive toolbar */
  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.space.spacing10} ${theme.space.spacing10}`};

    ${({ $stackOnMobile }) =>
      $stackOnMobile &&
      `
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      /* Stack toolbar items vertically */
      > * {
        width: 100%;
      }
    `}
  }
`;

const StyledSidebar = styled.div<StyledProps>`
  /* Sidebar styling with responsive behavior */
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  /* Background and borders */
  background-color: ${({ theme }) => theme.colors.palette.white};

  ${({ $position, theme }) =>
    $position === 'left' ? `border-right: 1px solid ${theme.colors.borderBase};` : `border-left: 1px solid ${theme.colors.borderBase};`}

  /* Mobile overlay positioning */
  ${({ $isMobile, $position }) =>
    $isMobile &&
    `
    position: fixed;
    top: 0;
    bottom: 0;
    width: 280px;
    z-index: 1000;
    transform: translateX(${$position === 'left' ? '-100%' : '100%'});
    transition: transform 0.3s ease-in-out;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    &.open {
      transform: translateX(0);
    }
  `}
`;

const StyledViewerContent = styled.div<StyledProps>`
  /* Main viewer content area */
  min-height: 0;

  /* Conditional overflow and height behavior for viewers */
  overflow: ${({ $hideOverflow }) => ($hideOverflow ? 'hidden' : 'auto')};
  height: ${({ $fullHeight }) => ($fullHeight ? '100%' : 'auto')};

  /* Ensure content fills available space */
  display: grid;
  grid-template-rows: 1fr;

  /* Allow viewer content to control its own layout */
  > * {
    min-height: 0;
  }
`;

const StyledFooter = styled.div<StyledProps>`
  /* Footer area for actions/status */
  padding: ${({ theme }) => `${theme.space.spacing10} ${theme.space.spacing20}`};
  border-top: 1px solid ${({ theme }) => theme.colors.borderBase};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};

  /* Mobile adjustments */
  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.space.spacing10} ${theme.space.spacing10}`};
  }
`;

const StyledMobileOverlay = styled.div<{ $position: 'left' | 'right' }>`
  /* Mobile overlay backdrop */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease-in-out;

  &.open {
    opacity: 1;
    pointer-events: auto;
  }
`;
